<template>
  <div id="app-shell">
    <div class="bg-grid" aria-hidden="true" />
    <div class="bg-glow" aria-hidden="true" />

    <CursorGlow />

    <div ref="overlay" class="route-overlay" aria-hidden="true">
      <span class="overlay-cursor" />
    </div>

    <header class="site-header">
      <router-link to="/" class="brand">
        <span ref="brandText" class="brand-text">Alexis<span class="dot">.</span>Mayeur</span>
        <span class="brand-cursor" />
      </router-link>
      <nav ref="nav" @mouseleave="resetIndicator">
        <router-link
          v-for="link in links"
          :key="link.to"
          :ref="el => setLinkRef(el, link.to)"
          :to="link.to"
          @mouseenter="moveIndicator(link.to)"
        >
          {{ link.label }}
        </router-link>
        <span ref="indicator" class="nav-indicator" />
      </nav>
    </header>

    <router-view v-slot="{ Component }">
      <transition name="page" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>

    <footer class="site-footer">
      <span>&copy; {{ new Date().getFullYear() }} Alexis Mayeur</span>
      <a href="https://github.com/vaticui" target="_blank" rel="noopener noreferrer" class="github-link">GitHub</a>
    </footer>
  </div>
</template>

<script>
import gsap from 'gsap'
import CursorGlow from '@/components/CursorGlow.vue'
import { registerTransitionHandlers } from '@/composables/pageTransition'
import { prefersReducedMotion } from '@/composables/useReducedMotion'

export default {
  name: 'App',
  components: { CursorGlow },
  data () {
    return {
      links: [
        { to: '/', label: 'Accueil' },
        { to: '/projets', label: 'Projets' }
      ],
      linkRefs: {}
    }
  },
  mounted () {
    window.addEventListener('keydown', this.handleShortcut)
    window.addEventListener('resize', this.syncIndicatorToRoute)

    registerTransitionHandlers({
      cover: this.playCover,
      reveal: this.playReveal
    })

    if (!prefersReducedMotion()) {
      gsap.fromTo(this.$refs.brandText,
        { clipPath: 'inset(0 100% 0 0)' },
        { clipPath: 'inset(0 0% 0 0)', duration: 0.7, ease: 'steps(12)', delay: 0.1 }
      )
    }

    this.$nextTick(this.syncIndicatorToRoute)
  },
  beforeUnmount () {
    window.removeEventListener('keydown', this.handleShortcut)
    window.removeEventListener('resize', this.syncIndicatorToRoute)
  },
  watch: {
    '$route' () {
      this.$nextTick(this.syncIndicatorToRoute)
    }
  },
  methods: {
    handleShortcut (e) {
      const tag = (e.target.tagName || '').toLowerCase()
      const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable
      if (isEditable) return

      if (e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey && e.key.toLowerCase() === 't') {
        e.preventDefault()
        if (this.$route.path !== '/admin/login') {
          this.$router.push('/admin/login')
        }
      }
    },
    setLinkRef (el, to) {
      if (el) this.linkRefs[to] = el.$el || el
    },
    moveIndicator (to) {
      const el = this.linkRefs[to]
      if (!el || !this.$refs.indicator) return
      const navRect = this.$refs.nav.getBoundingClientRect()
      const rect = el.getBoundingClientRect()
      gsap.to(this.$refs.indicator, {
        x: rect.left - navRect.left,
        width: rect.width,
        opacity: 1,
        duration: 0.35,
        ease: 'power3.out'
      })
    },
    resetIndicator () {
      this.syncIndicatorToRoute()
    },
    syncIndicatorToRoute () {
      const active = this.links.find(l => l.to === this.$route.path) || this.links[0]
      const el = this.linkRefs[active.to]
      if (!el || !this.$refs.indicator || !this.$refs.nav) return
      const navRect = this.$refs.nav.getBoundingClientRect()
      const rect = el.getBoundingClientRect()
      gsap.to(this.$refs.indicator, {
        x: rect.left - navRect.left,
        width: rect.width,
        opacity: this.$route.path === active.to ? 1 : 0.4,
        duration: 0.3,
        ease: 'power3.out'
      })
    },
    playCover () {
      if (prefersReducedMotion()) return Promise.resolve()
      return new Promise(resolve => {
        gsap.set(this.$refs.overlay, { transformOrigin: 'left center' })
        gsap.fromTo(this.$refs.overlay,
          { scaleX: 0 },
          { scaleX: 1, duration: 0.35, ease: 'power4.inOut', onComplete: resolve }
        )
      })
    },
    playReveal () {
      if (prefersReducedMotion()) return Promise.resolve()
      return new Promise(resolve => {
        gsap.set(this.$refs.overlay, { transformOrigin: 'right center' })
        gsap.to(this.$refs.overlay, {
          scaleX: 0,
          duration: 0.45,
          delay: 0.05,
          ease: 'power4.inOut',
          onComplete: resolve
        })
      })
    }
  }
}
</script>

<style>
:root {
  --bg: #0d1117;
  --bg-alt: #161b22;
  --border: #21262d;
  --text: #e6edf3;
  --text-dim: #8b949e;
  --accent: #3fb950;
  --accent-alt: #58a6ff;
}

* {
  box-sizing: border-box;
}

html, body {
  margin: 0;
  padding: 0;
  background: var(--bg);
}

body {
  font-family: 'Segoe UI', Avenir, Helvetica, Arial, sans-serif;
}

#app {
  color: var(--text);
}

#app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(230, 237, 243, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(230, 237, 243, 0.035) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%);
}

.bg-glow {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(680px circle at 12% -10%, rgba(63, 185, 80, 0.14), transparent 55%),
    radial-gradient(620px circle at 88% 10%, rgba(88, 166, 255, 0.12), transparent 55%);
  animation: drift 18s ease-in-out infinite alternate;
}

@media (prefers-reduced-motion: reduce) {
  .bg-glow {
    animation: none;
  }
}

@keyframes drift {
  from { transform: translate3d(0, 0, 0) scale(1); }
  to { transform: translate3d(-2%, 3%, 0) scale(1.08); }
}

.route-overlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  background: var(--bg);
  transform: scaleX(0);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.overlay-cursor {
  width: 14px;
  height: 28px;
  background: var(--accent);
  animation: blink 0.8s step-end infinite;
}

@keyframes fade-in-down {
  from {
    opacity: 0;
    transform: translateY(-12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.page-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease, filter 0.35s ease;
}

.page-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(14px);
  filter: blur(4px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (prefers-reduced-motion: reduce) {
  .page-enter-active, .page-leave-active {
    transition: opacity 0.15s ease;
  }
  .page-enter-from, .page-leave-to {
    transform: none;
    filter: none;
  }
}

.site-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 6vw;
  border-bottom: 1px solid var(--border);
  animation: fade-in-down 0.5s ease both;
}

.brand {
  display: inline-flex;
  align-items: center;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  text-decoration: none;
}

.brand-text {
  display: inline-block;
}

.brand .dot {
  color: var(--accent);
}

.brand-cursor {
  display: inline-block;
  width: 8px;
  height: 1em;
  margin-left: 4px;
  background: var(--accent);
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.site-header nav {
  position: relative;
  display: flex;
  gap: 28px;
}

.site-header nav a {
  position: relative;
  color: var(--text-dim);
  text-decoration: none;
  font-size: 0.95rem;
  padding-bottom: 4px;
  transition: color 0.2s ease;
}

.site-header nav a:hover {
  color: var(--text);
}

.site-header nav a.router-link-exact-active {
  color: var(--accent);
}

.nav-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: 0;
  background: var(--accent);
  opacity: 0;
  will-change: transform, width;
}

.site-footer {
  position: relative;
  z-index: 1;
  margin-top: auto;
  padding: 24px 6vw;
  border-top: 1px solid var(--border);
  color: var(--text-dim);
  font-size: 0.85rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.github-link {
  color: var(--text-dim);
  text-decoration: none;
  transition: color 0.2s ease;
}

.github-link:hover {
  color: var(--accent-alt);
}

main {
  position: relative;
  z-index: 1;
}
</style>
